import { forwardRef, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/utils/helpers";
import StageTrack from "../components/StageTrack";
import {
  WEEKDAY_SHORT,
  dayShort,
  matchesFilters,
  pillarHue,
  postsByDay,
  stageWord,
  weekCells,
  type CalendarFilters,
  type CalendarPost,
} from "./calendarModel";
import { formatLabel, peekHandlers, type PeekIntent } from "./CalendarPostCard";
import { ChannelMarks, TurnLine } from "./CalendarParts";
import { DayNumeral, liftKeys, useLift, type GridCallbacks } from "./MonthGrid";

/**
 * CalendarWeekCard (DESIGN.md "The Calendar in Parts"): the week's bigger card.
 * The cover once a file is uploaded, the title, format and channels, the stage
 * track, the stage word and whose turn it is.
 */
export const CalendarWeekCard = forwardRef<
  HTMLDivElement,
  {
    post: CalendarPost;
    isGuest: boolean;
    dimmed?: boolean;
    lifted?: boolean;
    active?: boolean;
    quiet?: boolean;
    coverUrl?: string | null;
    fileCount?: number | null;
    draggable?: boolean;
    onOpen: (anchor: HTMLElement) => void;
    peek?: PeekIntent;
    onLift?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
    onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
    onDragEnd?: () => void;
  }
>(function CalendarWeekCard(
  { post, isGuest, dimmed, lifted, active, quiet, coverUrl, fileCount, draggable, onOpen, peek, onLift, onKeyDown, onDragStart, onDragEnd },
  ref,
) {
  const { slot, state } = post;
  const hue = pillarHue(post.pillarIndex);
  const cover = slot.deliverable && coverUrl ? coverUrl : null;
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={dimmed ? -1 : 0}
      data-post-id={slot.id}
      aria-label={`${slot.title}. ${formatLabel(slot)}. ${stageWord(state, isGuest)}, ${state.turn.label}.`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(event) => onOpen(event.currentTarget)}
      {...peekHandlers(peek)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Enter") {
          event.preventDefault();
          onOpen(event.currentTarget);
        } else if (event.key === " " && onLift) {
          event.preventDefault();
          onLift();
        }
      }}
      className={cn(
        // Calendar / Week card: radius 12 on the surface, no hairline, the pillar a 3px edge.
        "relative flex w-full min-w-0 cursor-pointer flex-col gap-2.5 overflow-hidden rounded-card bg-co-surface px-2.5 pb-3 pt-2.5 text-left outline-none",
        "transition-[box-shadow,opacity] duration-[var(--duration-short)] ease-[var(--ease-out-expo)]",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-co-accent",
        active && "shadow-[inset_0_0_0_2px_var(--co-accent)]",
        dimmed && "opacity-30",
        lifted && "opacity-40",
      )}
    >
      {hue ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: hue }} />
      ) : null}
      {cover ? (
        <span className="relative block aspect-[159/254] w-full overflow-hidden rounded-card bg-co-recess">
          <img src={cover} alt="" className="size-full object-cover" loading="lazy" />
          {fileCount ? (
            <span className="absolute bottom-2 left-2 inline-flex h-[22px] items-center rounded-tag bg-co-scrim px-[7px] text-[12.5px] text-white">
              {fileCount} {fileCount === 1 ? "file" : "files"}
            </span>
          ) : null}
        </span>
      ) : null}
      <span className={cn("line-clamp-5 text-[14px] font-semibold leading-[1.32]", quiet ? "text-co-text2" : "text-co-text")}>
        {slot.title}
      </span>
      <span className="flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[17px] text-co-muted">{formatLabel(slot)}</span>
        <ChannelMarks channels={post.channels} className="gap-1" />
      </span>
      <span className="flex flex-col gap-1.5">
        <StageTrack stageIndex={state.stageIndex} stops={state.stops} size="card" className="gap-[3px]" />
        <span className="text-[12px] font-semibold text-co-text">{stageWord(state, isGuest)}</span>
      </span>
      <TurnLine owner={state.turn.owner} label={state.turn.label} alert={state.turn.alert} className="items-center" />
    </div>
  );
});

/**
 * The week: seven columns, a day heading each with its +, and the week's posts
 * as bigger cards. Days outside the open month show their posts read only.
 */
export default function WeekGrid({
  year,
  month,
  monthName,
  weekStart,
  posts,
  neighbourPosts,
  filters,
  todayUtc,
  closed,
  canAdd,
  isGuest,
  clientName,
  activePostId,
  callbacks,
  days = 7,
}: {
  /** 7, or 3 on a tablet. */
  days?: number;
  year: number;
  month: number;
  monthName: string;
  weekStart: number;
  posts: CalendarPost[];
  /** Posts of the neighbouring months, for the days of this week outside the open month. */
  neighbourPosts: CalendarPost[];
  filters: CalendarFilters;
  todayUtc: number;
  closed: boolean;
  canAdd: boolean;
  isGuest: boolean;
  clientName?: string | null;
  activePostId: string | null;
  callbacks: GridCallbacks;
}) {
  const cells = useMemo(() => weekCells(weekStart, year, month, days), [weekStart, year, month, days]);
  const byDay = useMemo(() => postsByDay(posts), [posts]);
  const neighbourByDay = useMemo(() => postsByDay(neighbourPosts), [neighbourPosts]);
  const canMove = !closed && !!callbacks.onMove;
  const { lift, setLift, ruleFor, drop } = useLift({
    monthName,
    closed,
    clientName,
    onMove: callbacks.onMove,
    onLiftChange: callbacks.onLiftChange,
  });
  const [pointerOver, setPointerOver] = useState<string | null>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const overKey = lift?.mode === "keyboard" ? (lift.target?.key ?? null) : pointerOver;

  return (
    <div
      role="grid"
      aria-label={`Week of ${dayShort(cells[0])}`}
      className="grid min-h-[640px] flex-1 pb-6"
      style={{ gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))` }}
    >
      <div role="row" className="contents">
        {cells.map((cell) => {
          const own = cell.inMonth;
          const dayPosts = own ? (byDay.get(cell.key) ?? []) : (neighbourByDay.get(cell.key) ?? []);
          const isToday = cell.utc === todayUtc;
          const past = cell.utc < todayUtc;
          const addable = canAdd && !closed && own && !past;
          const rule = ruleFor(cell);
          const over = overKey === cell.key;
          return (
            <div
              key={cell.key}
              role="gridcell"
              aria-label={`${WEEKDAY_SHORT[cell.weekday]} ${cell.day}`}
              onFocusCapture={() => callbacks.onFocusDay?.(cell)}
              onMouseEnter={() => callbacks.onFocusDay?.(cell)}
              onDragOver={(event) => {
                if (!lift) return;
                if (rule?.allowed) {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }
                setPointerOver(cell.key);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setPointerOver(null);
                drop(cell, dayPosts.length);
              }}
              className={cn(
                // A day: pad 12/10, gap 12, hairlines on top and right (none after Saturday).
                "group/day relative flex min-w-0 flex-col gap-3 px-2.5 py-3",
                "shadow-[inset_0_1px_0_var(--co-line)] [&:not(:last-child)]:shadow-[inset_0_1px_0_var(--co-line),inset_-1px_0_0_var(--co-line)]",
                lift && rule?.allowed && "bg-co-accent-dim",
                over && lift && (rule?.allowed ? "outline outline-2 -outline-offset-2 outline-co-accent" : "outline outline-1 -outline-offset-1 outline-co-line2"),
              )}
            >
              <div className="flex items-end gap-2">
                {isToday ? (
                  <DayNumeral cell={cell} isToday quiet={false} size={30} />
                ) : (
                  <span
                    className={cn(
                      "font-section text-[30px] font-bold leading-none tabular-nums",
                      !own ? "text-co-faint" : past ? "text-co-muted" : "text-co-text",
                    )}
                  >
                    {cell.day}
                  </span>
                )}
                <span className={cn("text-[12.5px] leading-[17px]", own ? "text-co-muted" : "text-co-faint")}>
                  {WEEKDAY_SHORT[cell.weekday]}
                </span>
              </div>
              {dayPosts.map((post) => (
                <CalendarWeekCard
                  key={post.slot.id}
                  ref={(node) => {
                    if (node) cardRefs.current.set(post.slot.id, node);
                    else cardRefs.current.delete(post.slot.id);
                  }}
                  post={post}
                  isGuest={isGuest}
                  quiet={past}
                  dimmed={!matchesFilters(post, filters)}
                  lifted={lift?.post.slot.id === post.slot.id}
                  active={activePostId === post.slot.id}
                  draggable={own && canMove && post.slot.status !== "IN_CLIENT_REVIEW"}
                  onOpen={(anchor) => callbacks.onOpenPost(post, anchor)}
                  peek={callbacks.peekFor?.(post)}
                  onLift={own && canMove ? () => setLift({ post, mode: "keyboard", target: cell }) : undefined}
                  onKeyDown={(event) => {
                    if (liftKeys(event, lift, cells, 1, setLift, drop, (c) => (byDay.get(c.key) ?? []).length)) {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  onDragStart={(event) => {
                    event.dataTransfer.setData("text/plain", post.slot.id);
                    event.dataTransfer.effectAllowed = "move";
                    setLift({ post, mode: "pointer", target: null });
                  }}
                  onDragEnd={() => {
                    setPointerOver(null);
                    setLift(null);
                  }}
                />
              ))}
              {lift && lift.mode === "keyboard" && over && rule?.allowed && !dayPosts.some((p) => p.slot.id === lift.post.slot.id) ? (
                <div aria-hidden="true">
                  <CalendarWeekCard post={lift.post} isGuest={isGuest} active onOpen={() => undefined} />
                </div>
              ) : null}
              {addable && dayPosts.length === 0 && !lift ? (
                <button
                  type="button"
                  onClick={() => callbacks.onAddDay?.(cell)}
                  className="flex h-11 items-center gap-1.5 rounded-tip bg-co-hover px-3 text-left text-[12px] font-medium text-co-text2 opacity-0 outline-none transition-opacity group-hover/day:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-co-accent"
                >
                  <Plus className="size-3.5" aria-hidden="true" />
                  Add a post on {dayShort(cell)}
                </button>
              ) : null}
              {lift && over ? (
                <span role="status" className="text-[12px] leading-snug text-co-text">
                  {rule?.allowed ? rule.consequence : rule?.reason}
                </span>
              ) : null}
            </div>
          );
      })}
      </div>
    </div>
  );
}
